import React, { useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import { useOutletContext } from "react-router-dom";
import CommonTable from "../common/commonTable";
import GlobalLoader from "../common/GlobalLoader";
import Pagination from "../common/Pagination";
import "../../styles/institution/institutionStudent.scss";
import {
  getAllInstitutionCourse,
  getAllInstitutionStudent,
} from "../../helper/apicalls/institution";
import TeacherAssignForm from "../admin/TeacherAssignForm";
import StudentForm from "../admin/StudentForm";
import {
  createStudentInstitution,
  deleteStudentInstitution,
  getAllStudentInstitution,
  updateStudentInstitution,
  resetStudentInstitutionPassword,
} from "../../helper/apicalls/institution/institutionStudent";
import SuccessPopup from "../common/SuccessPopup";
import {
  getAllBatchInstitution,
  getAllBatchStudentInstitution,
  getNotAssignBatchStudentInstitution,
  updateAssignBatchStudentInstitution,
} from "../../helper/apicalls/institution/institutionBatch";
import StudentSelectForm from "../admin/StudentSelectForm";
import ResetPasswordPopup from "../common/ResetPasswordPopup";
import { getInstitutionProfile } from "../../helper/apicalls/profile";

const InstitutionStudent = ({ batch, batchId }) => {
  const authToken = Cookies.get("institution_token");
  const admin_authToken = Cookies.get("admin_token");
  const { setHeaderName, setIsShowSearch } = useOutletContext();
  const permissions = Cookies.get("permissions");
  let permissionsData = [];
  try {
    const parsed = permissions ? JSON.parse(permissions) : [];
    permissionsData = Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error("Error parsing permissions:", e);
  }
  const hasBatchPermission = permissionsData?.find(
    (permission) => permission?.permission_type === "batches"
  );
  const hasStudentPermission = permissionsData?.find(
    (permission) => permission?.permission_type === "student"
  );

  const [loader, setLoader] = useState(false);
  const [students, setStudents] = useState([]);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ status: "", grade: "" });
  const [institutionType, setInstitutionType] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("Student created successfully");
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isStudentPickerOpen, setIsStudentPickerOpen] = useState(false);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [institutionCourseData, setInstitutionCourseData] = useState([]);
  const [batchesList, setBatchesList] = useState([]);

  const searchDebounceRef = useRef(null);
  const handleSearchChange = (val) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(val);
    }, 300);
  };

  const [resetPopupOpen, setResetPopupOpen] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState(null);

  const handleResetPasswordClick = (row) => {
    setResetTargetUser(row);
    setResetPopupOpen(true);
  };

  const handleResetPasswordSubmit = async (newPassword) => {
    if (!resetTargetUser) return;
    setLoader(true);
    try {
      const res = await resetStudentInstitutionPassword(authToken, {
        password: newPassword,
      }, resetTargetUser.id);
      setSuccessMsg(res?.message || "Password reset successfully");
      setSuccessOpen(true);
    } catch (err) {
      console.error("Failed to reset password:", err);
      setSuccessMsg(err?.message || "Failed to reset password");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
      setResetPopupOpen(false);
      setResetTargetUser(null);
    }
  };

  const columns = useMemo(() => {
    const cols = [
      { field: "name", label: "Name" },
      { field: "email", label: "Email" },
      {
        field: "register_no",
        label: "Register No.",
        align: "center",
        render: (v) => (v ? v : "-"),
      },
    ];

    if (!batch) {
      cols.push(
        {
          field: "grade",
          label: "Grade",
          render: (v) => {
            if (!v) return "-";
            if (v === "1 year") return "1st Year";
            if (v === "2 year") return "2nd Year";
            if (v === "3 year") return "3rd Year";
            if (v === "4 year") return "4th Year";
            return v.charAt(0).toUpperCase() + v.slice(1);
          },
          align: "center",
        },
        {
          field: "batch_name",
          label: "Batch name",
          render: (_value, row) => {
            const name = row?.batch_name || row?.batch?.name || (typeof row?.batch === "string" ? row.batch : null);
            return name ? name.charAt(0).toUpperCase() + name.slice(1) : "-";
          },
          align: "center",
        },
        {
          field: "batch_assigned",
          label: "Batch",
          align: "center",
          render: (_value, row) => {
            const value = row?.batch_assigned !== undefined ? row.batch_assigned : row?.batch;

            return (
              <span
                className={`assign-status-badge ${
                  value ? "assign-status-active" : "assign-status-inactive"
                }`}
              >
                {value ? "Assigned" : "Not assigned"}
              </span>
            );
          },
        }
      );
    }

    cols.push({
      field: "disabled",
      label: "Status",
      align: "center",
      render: (isDisabled) => (
        <span
          className={`status-badge ${
            isDisabled ? "status-inactive" : "status-active"
          }`}
        >
          {isDisabled ? "Disabled" : "Active"}
        </span>
      ),
    });

    if (hasStudentPermission?.write) {
      cols.push({
        field: "reset_password",
        label: "Reset Password",
        align: "center",
        render: (_, row) => (
          <button
            className="reset-password-btn-table"
            onClick={(e) => {
              e.stopPropagation();
              handleResetPasswordClick(row);
            }}

          >
            Reset
          </button>
        ),
      });
    }

    return cols;
  }, [batch, hasStudentPermission]);

  const fetchInstitutionStudent = async () => {
    setLoader(true);
    try {
      const s = (filters?.status || "").trim();
      const params = { page, limit };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (s === "active") params.disabled = false;
      else if (s === "disabled") params.disabled = true;
      if (filters?.grade) params.grade = filters.grade;

      const res = await getAllStudentInstitution(authToken, params);
      setStudents(Array.isArray(res?.data) ? res.data : []);
      const tp = Math.ceil(Number(res.total) / limit) || 1;
      setTotalPages(tp);
    } catch (err) {
      console.error("Failed to fetch students:", err);
    } finally {
      setLoader(false);
    }
  };

  const fetchInstitutionCourse = async () => {
    try {
      const res = await getAllInstitutionCourse(authToken, undefined, {});
      setInstitutionCourseData(res[0]?.course || []);
    } catch (err) {
      console.error("Failed to fetch teachers:", err);
    } finally {
    }
  };

  const fetchInstitutionBatchStudent = async () => {
    setLoader(true);
    try {
      const s = (filters?.status || "").trim();
      const params = { page, limit };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (batchId) params.batchId = batchId;
      if (s === "active") params.disabled = false;
      else if (s === "disabled") params.disabled = true;
      if (filters?.grade) params.grade = filters.grade;

      const res = await getAllBatchStudentInstitution(authToken, params);
      setStudents(Array.isArray(res?.data) ? res.data : []);
      const tp = Math.ceil(Number(res.total) / limit) || 1;
      setTotalPages(tp);
    } catch (err) {
      console.error("Failed to fetch students:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleOpenAssign = () => {
    if (!selectedStudentIds?.length) return;
    setIsAssignOpen(true);
  };

  const handleAssignClose = () => {
    setIsAssignOpen(false);
    setSelectedStudentIds([]);
    setBatchesList([]);
  };

  const handleAssignSubmit = async ({ studentIds, courseId, batchId }) => {
    try {
      const res = await updateAssignBatchStudentInstitution(authToken, {
        batch_id: batchId,
        student_ids: studentIds,
      });
      setSuccessMsg(res?.message || "Students assigned successfully");
      setSuccessOpen(true);
      handleAssignClose();
      await fetchInstitutionStudent();
      setSelectedStudentIds([]);
    } catch (err) {
      console.error("Failed to assign students:", err);
    }
  };

  const fetchCourseBatch = async (key) => {
    setLoader(true);
    try {
      const res = await getAllBatchInstitution(authToken, key, {});
      setBatchesList(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch batches:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleEdit = (id, row) => {
    setCurrentStudent(row);
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    setLoader(true);
    try {
      if (!batch && !batchId) {
        await deleteStudentInstitution(authToken, id);
        setSuccessMsg("Student deleted successfully");
        setSuccessOpen(true);
        await fetchInstitutionStudent();
      } else {
        const removeStudent = await updateAssignBatchStudentInstitution(
          authToken,
          {
            batch_id: batchId,
            remove_student_ids: [id],
          }
        );
        setSuccessMsg("Student removed successfully");
        setSuccessOpen(true);
        await fetchInstitutionBatchStudent();
      }
    } catch (err) {
      console.error("Failed to delete student:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleCreateClick = () => {
    setCurrentStudent(null);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setCurrentStudent(null);
  };

  const handleFormSubmit = async (data) => {
    setLoader(true);
    try {
      if (currentStudent) {
        const payload = {
          name: data.name,
          ...(data?.email !== "" ? { email: data.email } : {}),
          ...(data?.register_no !== ""
            ? { register_no: data.register_no }
            : {}),
          password: data.password,
          grade: data.grade,
        };
        const res = await updateStudentInstitution(
          authToken,
          payload,
          currentStudent.id
        );
        setSuccessMsg(res?.message || "Student updated successfully");
        setSuccessOpen(true);
        handleFormClose();
      } else {
        const payload = {
          name: data.name,
          ...(data?.email !== "" ? { email: data.email } : {}),
          ...(data?.register_no !== ""
            ? { register_no: data.register_no }
            : {}),
          password: data.password,
          grade: data.grade,
        };

        const res = await createStudentInstitution(authToken, payload);
        handleFormClose();
        setSuccessMsg(res?.message || "Student created successfully");
        setSuccessOpen(true);
      }
      await fetchInstitutionStudent();
    } catch (err) {
      console.error("Failed to create student:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleAddStudentClick = async () => {
    setLoader(true);
    try {
      const allRes = await getNotAssignBatchStudentInstitution(authToken);
      setAvailableStudents(allRes);
    } catch (err) {
      console.error("Failed to load students for selection:", err);
    } finally {
      setLoader(false);
      setIsStudentPickerOpen(true);
    }
  };

  const handleAddStudentClose = () => setIsStudentPickerOpen(false);

  const handleAddStudentSubmit = async ({ studentIds }) => {
    if (!Array.isArray(studentIds) || studentIds.length === 0) return;
    setLoader(true);
    try {
      const res = await updateAssignBatchStudentInstitution(authToken, {
        batch_id: batchId,
        student_ids: studentIds,
      });
      setSuccessMsg(res?.message || "Students assigned successfully");
      setSuccessOpen(true);
      setIsStudentPickerOpen(false);
      await fetchInstitutionBatchStudent();
    } catch (err) {
      console.error("Failed to assign students:", err);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await getInstitutionProfile(authToken);
        const type = response?.data?.institution?.type || response?.institution?.type || "";
        setInstitutionType(type);
      } catch (err) {
        console.error("Failed to fetch institution profile:", err);
      }
    };
    if (authToken) {
      fetchProfileData();
    }
  }, [authToken]);

  useEffect(() => {
    if (!batch) {
      setHeaderName && setHeaderName("Students");
    }
    setIsShowSearch && setIsShowSearch(false);
  }, []);

  useEffect(() => {
    if (batch && batchId) {
      fetchInstitutionBatchStudent();
      return;
    } else {
      fetchInstitutionCourse();
      fetchInstitutionStudent();
    }
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [page, limit, searchTerm, filters]);

  const goToPage = (p) => {
    const next = Math.max(1, p);
    setLoader(true);
    setPage(next);
  };

  const changePerPage = (pp) => {
    setLoader(true);
    setLimit(pp);
    setPage(1);
  };

  return (
    <div className="institution-student-tab-container">
      {loader && <GlobalLoader visible={loader} text="Loading students..." />}
      <div className="institution-student-tab">
        <CommonTable
          data={students}
          columns={columns}
          getRowId={(r) => r.id}
          showToolbar
          title={"Students List"}
          onSearchChange={handleSearchChange}
          showFilter
          filterSections={[
            {
              key: "status",
              title: "Status",
              multiple: false,
              options: [
                { value: "", label: "All" },
                { value: "active", label: "Active" },
                { value: "disabled", label: "Disabled" },
              ],
            },
            {
              key: "grade",
              title: "Grade",
              multiple: false,
              options:
                institutionType === "college"
                  ? [
                      { value: "", label: "All" },
                      { value: "1 year", label: "1st Year" },
                      { value: "2 year", label: "2nd Year" },
                      { value: "3 year", label: "3rd Year" },
                      { value: "4 year", label: "4th Year" },
                    ]
                  : [
                      { value: "", label: "All" },
                      { value: "6", label: "6" },
                      { value: "7", label: "7" },
                      { value: "8", label: "8" },
                      { value: "9", label: "9" },
                      { value: "10", label: "10" },
                      { value: "11", label: "11" },
                      { value: "12", label: "12" },
                    ],
            },
          ]}
          toolbarRight={
            <>
              {!batch && hasStudentPermission?.write && (
                <>
                  <button
                    className="institution-student-tab-header-btn btn-interactive"
                    onClick={handleCreateClick}
                  >
                    Create
                  </button>
                  <button
                    className="institution-student-tab-header-btn btn-interactive"
                    onClick={handleOpenAssign}
                    disabled={
                      !(selectedStudentIds && selectedStudentIds?.length > 0)
                    }
                  >
                    Assign
                  </button>
                </>
              )}
              {batch && hasBatchPermission?.write && (
                <button
                  className="institution-student-tab-header-btn btn-interactive"
                  onClick={handleAddStudentClick}
                >
                  Add Students
                </button>
              )}
            </>
          }
          isRowSelectable={(row) => {
            const value = row?.batch_assigned !== undefined ? row.batch_assigned : row?.batch;
            return !value;
          }}
          filterValues={filters}
          onFilterChange={setFilters}
          selectedRowIds={selectedStudentIds}
          onSelectionChange={setSelectedStudentIds}
          multiSelect={
            batch
              ? false
              : !batch && !hasStudentPermission?.write
              ? false
              : true
          }
          maxBodyHeight={
            batch ? `calc(-300px + 100vh)` : `calc(-230px + 100vh)`
          }
          fixedBodyRows={8}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isConfirmDeleteOpen={isConfirmDeleteOpen}
          setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
          actionLabelDelete={batch ? "Remove" : "Delete"}
          showActions={
            batch && !hasBatchPermission?.write
              ? false
              : !batch && !hasStudentPermission?.write
              ? false
              : true
          }
          deleteMessage={
            batch
              ? "Are you sure you want to remove from this batch? This action cannot be undone."
              : "Are you sure you want to delete this item? This action cannot be undone."
          }
          removeActionLabel={batch ? "Remove from Batch" : "Delete"}
          showEditAction={batch ? false : true}
        />
      </div>
      <div className="institution-student-pagination">
        <Pagination
          page={page}
          pageCount={totalPages}
          onChange={goToPage}
          perPage={limit}
          perPageOptions={[5, 10, 25, 100]}
          onPerPageChange={changePerPage}
        />
      </div>
      {isAssignOpen && (
        <TeacherAssignForm
          isOpen={isAssignOpen}
          onClose={handleAssignClose}
          onSubmit={handleAssignSubmit}
          selectedIds={selectedStudentIds}
          courses={institutionCourseData}
          batches={batchesList}
          title="Assign Students"
          idKey="studentIds"
          getBatched={fetchCourseBatch}
        />
      )}
      {isFormOpen && (
        <StudentForm
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          initialData={currentStudent}
          type={institutionType}
        />
      )}
      {isStudentPickerOpen && (
        <StudentSelectForm
          isOpen={isStudentPickerOpen}
          onClose={handleAddStudentClose}
          onSubmit={handleAddStudentSubmit}
          students={availableStudents}
          title="Add Students"
        />
      )}
      {successOpen && (
        <SuccessPopup
          open={successOpen}
          onClose={() => setSuccessOpen(false)}
          message={successMsg}
        />
      )}
      {resetPopupOpen && resetTargetUser && (
        <ResetPasswordPopup
          isOpen={resetPopupOpen}
          onClose={() => {
            setResetPopupOpen(false);
            setResetTargetUser(null);
          }}
          userName={resetTargetUser.name}
          onResetPassword={handleResetPasswordSubmit}
        />
      )}
    </div>
  );
};

export default InstitutionStudent;
